const POINTS_TABLE = [25, 18, 15, 12, 10, 8, 6, 4, 2, 1];

export function aceCompositeId(value) {
  if (!value || typeof value !== "object") return "";
  return `${value.a ?? ""}:${value.b ?? ""}`;
}

export function normalizePlayerId(value) {
  const id = String(value ?? "");
  return id.startsWith("S") ? id.slice(1) : id;
}

export function toAppDriverId(value) {
  const id = String(value ?? "").trim();
  if (!id || id === "â€”") return "";
  return id.startsWith("S") ? id : `S${id}`;
}

function driverName(driver) {
  return [driver?.first_name, driver?.last_name].filter(Boolean).join(" ") || driver?.nickname || "Driver";
}

function pointsForRow(json, index) {
  const points = json?.driver_points?.[index];
  if (Number.isFinite(points)) return points;
  if (points && Number.isFinite(points.points)) return points.points;
  return POINTS_TABLE[index] ?? 0;
}

export function aceRaceRows(json) {
  if (!json || String(json.session_type || "").toLowerCase() !== "race") return [];

  const driversByGuid = new Map(
    (Array.isArray(json.drivers) ? json.drivers : []).map((driver) => [aceCompositeId(driver.guid), driver])
  );
  const carsById = new Map(
    (Array.isArray(json.cars) ? json.cars : []).map((car) => [aceCompositeId(car.car_id), car])
  );
  const carStandings = Array.isArray(json.car_standings) ? json.car_standings : [];
  const driverCarIds = new Map();
  (Array.isArray(json.laps) ? json.laps : []).forEach((lap) => {
    const driverId = aceCompositeId(lap.driver_key);
    if (driverId && !driverCarIds.has(driverId)) driverCarIds.set(driverId, aceCompositeId(lap.car_key));
  });

  return (Array.isArray(json.driver_standings) ? json.driver_standings : [])
    .map((standing, index) => {
      const driver = driversByGuid.get(aceCompositeId(standing));
      if (!driver) return null;
      const driverGuid = aceCompositeId(standing);
      const carId = driverCarIds.get(driverGuid) || aceCompositeId(carStandings[index]?.car_id);
      const car = carsById.get(carId) || {};
      const carStanding = carStandings.find((entry) => aceCompositeId(entry.car_id) === carId)
        || carStandings[index]
        || {};
      return {
        id: normalizePlayerId(driver.player_id),
        name: driverName(driver),
        position: index + 1,
        points: pointsForRow(json, index),
        car: car.model_displayname || "",
        raceNumber: car.race_number,
        startingPosition: carStanding.starting_position,
        totalKm: carStanding.total_km,
        fuelLiters: carStanding.total_fuel_liters,
        energyConsumed: carStanding.energy_source_consumed,
        energySourceType: carStanding.energy_source_type,
      };
    })
    .filter(Boolean);
}

export function aceSessionRow(json, playerId, fileName) {
  if (!json) return null;

  const wantedId = normalizePlayerId(playerId);
  const driver = (Array.isArray(json.drivers) ? json.drivers : []).find(
    (entry) => normalizePlayerId(entry?.player_id) === wantedId
  );
  if (!driver) return null;

  const driverGuid = aceCompositeId(driver.guid);
  const laps = (Array.isArray(json.laps) ? json.laps : []).filter(
    (lap) => aceCompositeId(lap?.driver_key) === driverGuid
  );
  const validLapTimes = laps
    .map((lap) => lap?.time)
    .filter((time) => Number.isFinite(time) && time > 0);
  const sessionType = String(json.session_type || "");
  const isRace = sessionType.toLowerCase() === "race";
  const raceResult = isRace
    ? aceRaceRows(json).find((result) => normalizePlayerId(result.id) === wantedId)
    : null;

  return {
    fileName,
    sim: "AC_EVO",
    playerId: toAppDriverId(driver.player_id),
    session: sessionType || "Practice",
    track: [json.track_name, json.track_layout_name].filter(Boolean).join(" — ") || "—",
    best: validLapTimes.length ? Math.min(...validLapTimes) : null,
    laps: laps.length || null,
    position: raceResult?.position ?? null,
  };
}

export function buildAceStandings(sessions, profiles = [], dropRounds = 0) {
  const profileById = new Map(profiles.map((profile) => [normalizePlayerId(profile.id), profile]));
  const totals = new Map();
  const races = (sessions || []).filter(
    (session) => !session?.__error && String(session?.session_type || "").toLowerCase() === "race"
  );

  races.forEach((race) => {
    aceRaceRows(race).forEach((result) => {
      const current = totals.get(result.id) || {
        id: result.id,
        name: result.name,
        pointsByRound: [],
        wins: 0,
        podiums: 0,
      };
      current.name = result.name || current.name;
      current.car = result.car || current.car;
      current.number = result.raceNumber ?? current.number;
      current.pointsByRound.push(result.points);
      current.wins += result.position === 1 ? 1 : 0;
      current.podiums += result.position <= 3 ? 1 : 0;
      totals.set(result.id, current);
    });
  });

  const drivers = Array.from(totals.values()).map((result) => {
    const profile = profileById.get(result.id) || {};
    const grossPoints = result.pointsByRound.reduce((sum, points) => sum + points, 0);
    const droppedPoints = [...result.pointsByRound]
      .sort((a, b) => a - b)
      .slice(0, Math.max(0, dropRounds))
      .reduce((sum, points) => sum + points, 0);
    return {
      ...profile,
      ...result,
      id: profile.id || result.id,
      name: profile.name || result.name,
      team: profile.team || "Private",
      car: result.car || profile.car || "",
      points: grossPoints - droppedPoints,
      current_points: grossPoints - droppedPoints,
      gross_points: grossPoints,
      dropped_points: droppedPoints,
      current_wins: result.wins,
      current_podiums: result.podiums,
    };
  });

  const teamsByName = new Map();
  drivers.forEach((driver) => {
    const name = driver.team || "Private";
    const team = teamsByName.get(name) || { name, points: 0, drivers: [] };
    team.points += driver.current_points;
    team.drivers.push(driver.id);
    teamsByName.set(name, team);
  });

  return {
    drivers: drivers.sort((a, b) => b.current_points - a.current_points || a.name.localeCompare(b.name)),
    teams: Array.from(teamsByName.values()).sort((a, b) => b.points - a.points),
    races,
  };
}
